import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export const PaymentSuccessPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = React.useState('loading');
  const [message, setMessage] = React.useState('Verifying payment...');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setMessage('No payment session found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setStatus('timeout');
      setMessage('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await api().get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Thank you for your payment.');
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Error verifying payment status.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-zinc-900/50 border border-zinc-800 p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold uppercase mb-2">Processing Payment</h1>
            <p className="text-zinc-400">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold uppercase mb-2 text-emerald-400">Payment Complete</h1>
            <p className="text-zinc-400 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/client-portal')}
              data-testid="back-to-portal-btn"
              className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm"
            >
              Back to Portal
            </Button>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold uppercase mb-2 text-red-400">
              {status === 'timeout' ? 'Verification Timeout' : 'Payment Failed'}
            </h1>
            <p className="text-zinc-400 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/client-portal')}
              data-testid="back-to-portal-btn"
              className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm"
            >
              Back to Portal
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
